import React, { useEffect, useState, useCallback } from "react";
import { Link, Navigate } from "react-router-dom";
import api from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { useLang } from "../../contexts/LangContext";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import {
  Store, Plus, Package, Boxes, Pencil, Trash2, ExternalLink,
  Megaphone, HeadphonesIcon, Loader2, Save, FileText, Download, UploadCloud,
  LayoutDashboard, Menu, LogOut, Home, Receipt, Image as ImageIcon, Copy,
} from "lucide-react";
import { ASSET } from "../../lib/api";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "../../components/ui/sheet";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";

const CATEGORIES = [
  "Vino", "Olio EVO", "Salumi", "Formaggi", "Pasta", "Conserve",
  "Dolci", "Caffè", "Caviale & Roe", "Tonno & Pesce", "Altro",
];

const inputCls =
  "w-full px-4 py-2.5 border border-border rounded-sm bg-card focus:outline-none focus:border-[hsl(var(--primary))] text-sm";

const EMPTY_PRODUCT = {
  name: "", description: "", category: "Vino", image_url: "",
  price: "", quantity: "", sale_mode: "pezzi", block_size: "", min_order_blocks: "1",
};

export default function VendorDashboard() {
  const { user, setUser, logout } = useAuth();
  const { formatPrice } = useLang();
  const [tab, setTab] = useState("panoramica");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [counts, setCounts] = useState({ b2c: 0, b2b: 0 });
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  // product dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // product id or null
  const [pform, setPform] = useState(EMPTY_PRODUCT);
  const [saving, setSaving] = useState(false);

  // store form
  const [store, setStore] = useState({ store_name: "", store_description: "", logo_url: "", category: "", city: "" });
  const [storeSaving, setStoreSaving] = useState(false);

  // service form
  const [svc, setSvc] = useState({ service_type: "marketing", message: "", budget: "", product_id: "" });
  const [svcSaving, setSvcSaving] = useState(false);

  // documents
  const [docLibrary, setDocLibrary] = useState([]);
  const [myDocs, setMyDocs] = useState([]);
  const [docUploading, setDocUploading] = useState(false);
  const [docLabel, setDocLabel] = useState("");
  const [docType, setDocType] = useState("contratto_firmato");
  const fileInputRef = React.useRef(null);

  // orders received
  const [orders, setOrders] = useState([]);
  const [ordersRevenue, setOrdersRevenue] = useState(0);

  // product image upload
  const [imgUploading, setImgUploading] = useState(false);
  const imgInputRef = React.useRef(null);

  const isVendorRole = user && ["buyer", "super_admin"].includes(user.role);

  const loadProducts = useCallback(() => {
    api.get("/vendor/products").then((r) => {
      setProducts(r.data.products || []);
      setCounts(r.data.by_channel || { b2c: 0, b2b: 0 });
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const loadServices = useCallback(() => {
    api.get("/vendor/services").then((r) => setServices(r.data.requests || [])).catch(() => {});
  }, []);

  const loadDocs = useCallback(() => {
    api.get("/vendor/documents").then((r) => {
      setDocLibrary(r.data.library || []);
      setMyDocs(r.data.documents || []);
    }).catch(() => {});
  }, []);

  const loadOrders = useCallback(() => {
    api.get("/vendor/orders").then((r) => {
      setOrders(r.data.orders || []);
      setOrdersRevenue(r.data.paid_revenue || 0);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isVendorRole) return;
    loadProducts();
    loadServices();
    loadDocs();
    loadOrders();
    setStore({
      store_name: user.store_name || user.company || "",
      store_description: user.store_description || "",
      logo_url: user.logo_url || "",
      category: user.category || "",
      city: user.city || "",
    });
  }, [isVendorRole, user, loadProducts, loadServices, loadDocs]);

  if (!user) return <Navigate to="/azienda/registrati" replace />;
  if (!isVendorRole) {
    return (
      <div className="container-xl py-24 text-center">
        <h1 className="editorial-heading text-3xl mb-2">Accesso riservato alle aziende</h1>
        <p className="text-muted-foreground mb-6">Il tuo account non è abilitato come fornitore.</p>
        <Link to="/azienda/registrati" className="text-[hsl(var(--primary))] hover:underline">Registra la tua azienda →</Link>
      </div>
    );
  }

  // ---- Product actions ----
  const openCreate = () => { setEditing(null); setPform(EMPTY_PRODUCT); setDialogOpen(true); };  const openEdit = (p) => {
    setEditing(p.id);
    setPform({
      name: p.name, description: p.description || "", category: p.category || "Vino",
      image_url: p.image_url || "", price: String(p.price), quantity: String(p.quantity),
      sale_mode: p.sale_mode, block_size: p.block_size ? String(p.block_size) : "",
      min_order_blocks: p.min_order_blocks ? String(p.min_order_blocks) : "1",
    });
    setDialogOpen(true);
  };

  const openDuplicate = (p) => {
    setEditing(null); // create as NEW product so sale_mode is selectable
    setPform({
      name: `${p.name} (blocco B2B)`,
      description: p.description || "",
      category: p.category || "Vino",
      image_url: p.image_url || "",
      price: String(p.price),
      quantity: String(p.quantity),
      sale_mode: "blocchi",           // default: vendita a blocchi per B2B
      block_size: "6",
      min_order_blocks: "1",
    });
    setDialogOpen(true);
    toast.info("Duplica: imposta blocco e prezzo, poi pubblica come prodotto B2B.");
  };

  const uploadProductImage = async (e) => {    const file = e.target.files?.[0];
    if (!file) return;
    setImgUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/vendor/upload-image", fd);
      setPform((f) => ({ ...f, image_url: r.data.url }));
      toast.success("Immagine caricata.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore upload immagine.");
    } finally {
      setImgUploading(false);
      if (imgInputRef.current) imgInputRef.current.value = "";
    }
  };

  const saveProduct = async () => {
    if (!pform.name || !pform.price || pform.quantity === "") {
      toast.error("Nome, prezzo e quantità sono obbligatori.");
      return;
    }
    if (pform.sale_mode === "blocchi" && (!pform.block_size || Number(pform.block_size) < 1)) {
      toast.error("Per la vendita a blocchi indica i pezzi per blocco.");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        const body = {
          name: pform.name, description: pform.description, category: pform.category,
          image_url: pform.image_url, price: Number(pform.price), quantity: Number(pform.quantity),
        };
        if (pform.sale_mode === "blocchi") {
          body.block_size = Number(pform.block_size);
          body.min_order_blocks = Number(pform.min_order_blocks || 1);
        }
        await api.put(`/vendor/products/${editing}`, body);
        toast.success("Prodotto aggiornato.");
      } else {
        const body = {
          name: pform.name, description: pform.description, category: pform.category,
          image_url: pform.image_url, price: Number(pform.price), quantity: Number(pform.quantity),
          sale_mode: pform.sale_mode,
        };
        if (pform.sale_mode === "blocchi") {
          body.block_size = Number(pform.block_size);
          body.min_order_blocks = Number(pform.min_order_blocks || 1);
        }
        await api.post("/vendor/products", body);
        toast.success("Prodotto pubblicato ed è già live!");
      }
      setDialogOpen(false);
      loadProducts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nel salvataggio.");
    } finally { setSaving(false); }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Ritirare questo prodotto dalla vendita?")) return;
    try {
      await api.delete(`/vendor/products/${id}`);
      toast.success("Prodotto ritirato.");
      loadProducts();
    } catch { toast.error("Errore."); }
  };

  // ---- Store ----
  const saveStore = async () => {
    setStoreSaving(true);
    try {
      const r = await api.put("/vendor/store", store);
      toast.success("Vetrina aggiornata.");
      // sync user
      const updated = { ...user, ...store, is_vendor: true, store_slug: r.data.store?.store_slug || user.store_slug };
      localStorage.setItem("ti_user", JSON.stringify(updated));
      setUser(updated);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore.");
    } finally { setStoreSaving(false); }
  };

  // ---- Service request ----
  const submitService = async (e) => {
    e.preventDefault();
    if (!svc.message || svc.message.length < 3) { toast.error("Scrivi un messaggio."); return; }
    setSvcSaving(true);
    try {
      await api.post("/vendor/services/request", {
        service_type: svc.service_type,
        message: svc.message,
        budget: svc.budget ? Number(svc.budget) : null,
        product_id: svc.product_id || null,
      });
      toast.success("Richiesta inviata! Ti contatteremo presto.");
      setSvc({ service_type: "marketing", message: "", budget: "", product_id: "" });
      loadServices();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore.");
    } finally { setSvcSaving(false); }
  };

  const set = (k) => (e) => setPform((f) => ({ ...f, [k]: e.target.value }));
  const TABS = [
    { k: "panoramica", label: "Panoramica", icon: LayoutDashboard },
    { k: "prodotti", label: "I miei prodotti", icon: Package },
    { k: "ordini", label: "Ordini ricevuti", icon: Receipt },
    { k: "vetrina", label: "La mia vetrina", icon: Store },
    { k: "documenti", label: "Documenti", icon: FileText },
    { k: "servizi", label: "Servizi", icon: Megaphone },
  ];
  const currentTab = TABS.find((tt) => tt.k === tab) || TABS[0];

  // ---- Documents ----
  const uploadDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setDocUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (docLabel) fd.append("label", docLabel);
      fd.append("doc_type", docType);
      await api.post("/vendor/documents/upload", fd);
      toast.success("Documento caricato.");
      setDocLabel("");
      if (fileInputRef.current) fileInputRef.current.value = "";
      loadDocs();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nel caricamento.");
    } finally { setDocUploading(false); }
  };

  const deleteDoc = async (id) => {
    if (!window.confirm("Eliminare questo documento?")) return;
    try {
      await api.delete(`/vendor/documents/${id}`);
      toast.success("Documento eliminato.");
      loadDocs();
    } catch { toast.error("Errore."); }
  };
  const fmtSize = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

  return (
    <div data-testid="vendor-dashboard-page" className="min-h-screen flex bg-white text-stone-900">
      {/* DESKTOP SIDEBAR */}
      <aside className="fixed left-0 top-0 h-screen w-[264px] bg-[#141210] border-r border-[#C9A961]/20 hidden lg:flex flex-col z-30">
        <div className="p-5 border-b border-white/10">
          <Link to="/" className="flex items-center gap-2">
            <img src="/mzn300-logo.png" alt="MZN 300" className="h-12 w-auto" />
          </Link>
          <div className="mt-3 text-[10px] uppercase tracking-[0.3em] text-[#C9A961]">Backend Azienda</div>
          <div className="text-white font-serif text-lg leading-tight truncate">{user.store_name || user.company || user.name}</div>
        </div>
        <div className="flex-1 overflow-y-auto p-3">
          <nav className="flex flex-col gap-1">
            {TABS.map((tt) => (
              <button
                key={tt.k}
                data-testid={`tab-${tt.k}`}
                onClick={() => setTab(tt.k)}
                className={`flex items-center gap-3 px-3 py-2.5 text-sm rounded-sm transition-colors ${
                  tab === tt.k ? "bg-[#C9A961] text-black font-semibold" : "text-white/70 hover:bg-white/10 hover:text-white"
                }`}
              >
                <tt.icon className="h-4 w-4" /> {tt.label}
              </button>
            ))}
          </nav>
        </div>
        <div className="p-3 border-t border-white/10 space-y-1">
          {user.store_slug && (
            <Link to={`/negozio/${user.store_slug}`} data-testid="view-store-link" className="flex items-center gap-3 px-3 py-2.5 text-sm rounded-sm text-white/70 hover:bg-white/10 hover:text-white">
              <ExternalLink className="h-4 w-4" /> Vedi vetrina
            </Link>
          )}
          <Link to="/" className="flex items-center gap-3 px-3 py-2.5 text-sm rounded-sm text-white/70 hover:bg-white/10 hover:text-white">
            <Home className="h-4 w-4" /> Sito principale
          </Link>
          <button data-testid="vendor-logout" onClick={logout} className="w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-sm text-white/70 hover:bg-red-500/20 hover:text-white">
            <LogOut className="h-4 w-4" /> Esci
          </button>
        </div>
      </aside>

      {/* MAIN */}
      <div className="flex-1 lg:ml-[264px] min-w-0 flex flex-col">
        {/* Top bar */}
        <header className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b border-[#C9A961]/25">
          <div className="px-4 md:px-8 h-16 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
                <SheetTrigger asChild>
                  <button data-testid="vendor-mobile-menu" className="lg:hidden p-2 rounded-sm hover:bg-black/5"><Menu className="h-5 w-5" /></button>
                </SheetTrigger>
                <SheetContent side="left" className="w-[280px] bg-[#141210] p-4 border-r border-[#C9A961]/20">
                  <VisuallyHidden><SheetTitle>Menu Azienda</SheetTitle></VisuallyHidden>
                  <img src="/mzn300-logo.png" alt="MZN 300" className="h-12 w-auto mb-4" />
                  <nav className="flex flex-col gap-1">
                    {TABS.map((tt) => (
                      <button
                        key={tt.k}
                        data-testid={`tab-m-${tt.k}`}
                        onClick={() => { setTab(tt.k); setMobileNavOpen(false); }}
                        className={`flex items-center gap-3 px-3 py-2.5 text-sm rounded-sm ${tab === tt.k ? "bg-[#C9A961] text-black font-semibold" : "text-white/70 hover:bg-white/10"}`}
                      >
                        <tt.icon className="h-4 w-4" /> {tt.label}
                      </button>
                    ))}
                    <button onClick={logout} className="mt-2 flex items-center gap-3 px-3 py-2.5 text-sm rounded-sm text-white/70 hover:bg-red-500/20"><LogOut className="h-4 w-4" /> Esci</button>
                  </nav>
                </SheetContent>
              </Sheet>
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#A88A4A]">Backend Azienda</div>
                <h1 className="editorial-heading text-xl md:text-2xl leading-none flex items-center gap-2">
                  <currentTab.icon className="h-5 w-5 text-[#C9A961]" />{currentTab.label}
                </h1>
              </div>
            </div>
            <button data-testid="new-product-btn" onClick={openCreate} className="inline-flex items-center gap-2 px-4 md:px-5 py-2.5 bg-[hsl(var(--primary))] text-white rounded-sm text-sm font-semibold hover:opacity-90">
              <Plus className="h-4 w-4" /> <span className="hidden sm:inline">Nuovo prodotto</span>
            </button>
          </div>
        </header>

        <div className="p-4 md:p-8 flex-1">
          {/* PANORAMICA */}
          {tab === "panoramica" && (
            <div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <div className="bg-white border border-[#C9A961]/20 rounded-sm p-5 shadow-sm">
                  <div className="text-3xl font-semibold editorial-heading">{products.length}</div>
                  <div className="text-xs text-stone-500 uppercase tracking-wider mt-1">Prodotti totali</div>
                </div>
                <div className="bg-white border border-[#C9A961]/20 rounded-sm p-5 shadow-sm">
                  <div className="text-3xl font-semibold editorial-heading text-[hsl(var(--primary))]">{counts.b2c || 0}</div>
                  <div className="text-xs text-stone-500 uppercase tracking-wider mt-1">A pezzi (B2C)</div>
                </div>
                <div className="bg-white border border-[#C9A961]/20 rounded-sm p-5 shadow-sm">
                  <div className="text-3xl font-semibold editorial-heading text-[#8a6d1f]">{counts.b2b || 0}</div>
                  <div className="text-xs text-stone-500 uppercase tracking-wider mt-1">A blocchi (B2B)</div>
                </div>
                <div className="bg-white border border-[#C9A961]/20 rounded-sm p-5 shadow-sm">
                  <div className="text-3xl font-semibold editorial-heading">{services.length}</div>
                  <div className="text-xs text-stone-500 uppercase tracking-wider mt-1">Richieste servizi</div>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { k: "prodotti", icon: Package, title: "Gestisci prodotti", desc: "Carica e modifica i tuoi articoli" },
                  { k: "ordini", icon: Receipt, title: "Ordini ricevuti", desc: "Vendite dei tuoi prodotti a pezzi" },
                  { k: "vetrina", icon: Store, title: "La tua vetrina", desc: "Logo, descrizione e dati negozio" },
                  { k: "documenti", icon: FileText, title: "Documenti", desc: "Contratti da scaricare e caricare" },
                  { k: "servizi", icon: Megaphone, title: "Servizi", desc: "Marketing e assistenza vendita" },
                ].map((q) => (
                  <button
                    key={q.k}
                    data-testid={`quick-${q.k}`}
                    onClick={() => setTab(q.k)}
                    className="text-left bg-white border border-[#C9A961]/20 rounded-sm p-5 shadow-sm hover:border-[#C9A961] hover:shadow-md transition-all"
                  >
                    <q.icon className="h-6 w-6 text-[#C9A961] mb-3" />
                    <div className="font-serif text-lg">{q.title}</div>
                    <div className="text-sm text-stone-500 mt-1">{q.desc}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

      {/* PRODOTTI */}
      {tab === "prodotti" && (
        loading ? (
          <div className="text-center py-16 text-muted-foreground">Caricamento…</div>
        ) : products.length === 0 ? (
          <div className="text-center py-16">
            <Package className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground mb-4">Non hai ancora caricato prodotti.</p>
            <button onClick={openCreate} className="text-[hsl(var(--primary))] hover:underline">Carica il primo prodotto →</button>
          </div>
        ) : (
          <div className="overflow-x-auto border border-border rounded-sm">
            <table className="w-full text-sm">
              <thead className="bg-[hsl(var(--muted))] text-left">
                <tr>
                  <th className="px-4 py-3 font-semibold">Prodotto</th>
                  <th className="px-4 py-3 font-semibold">Modalità</th>
                  <th className="px-4 py-3 font-semibold">Prezzo</th>
                  <th className="px-4 py-3 font-semibold">Quantità</th>
                  <th className="px-4 py-3 font-semibold">Stato</th>
                  <th className="px-4 py-3 font-semibold text-right">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.id} data-testid="product-row" className="border-t border-border hover:bg-[hsl(var(--muted))]/40">
                    <td className="px-4 py-3">
                      <div className="font-medium">{p.name}</div>
                      <div className="text-xs text-muted-foreground">{p.category}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-sm ${
                        p.sale_mode === "blocchi" ? "bg-[#0a0a0a] text-[#C9A961]" : "bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]"
                      }`}>
                        {p.sale_mode === "blocchi" ? <Boxes className="h-3 w-3" /> : <Package className="h-3 w-3" />}
                        {p.sale_mode === "blocchi" ? `Blocchi · B2B (${p.block_size}pz)` : "Pezzi · B2C"}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-semibold">{formatPrice ? formatPrice(p.price) : `€ ${p.price}`}</td>
                    <td className="px-4 py-3">{p.quantity}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded-sm ${
                        p.status === "active" ? "bg-emerald-100 text-emerald-700"
                        : p.status === "withdrawn" ? "bg-red-100 text-red-700"
                        : "bg-gray-100 text-gray-600"
                      }`}>
                        {p.status === "active" ? "Live" : p.status === "withdrawn" ? "Ritirato" : "Inattivo"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-2">
                        <button data-testid="duplicate-product-btn" onClick={() => openDuplicate(p)} className="p-2 hover:bg-[hsl(var(--muted))] rounded-sm text-[#8a6d1f]" title="Duplica come blocco B2B">
                          <Copy className="h-4 w-4" />
                        </button>
                        <button data-testid="edit-product-btn" onClick={() => openEdit(p)} className="p-2 hover:bg-[hsl(var(--muted))] rounded-sm" title="Modifica">
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button data-testid="delete-product-btn" onClick={() => deleteProduct(p.id)} className="p-2 hover:bg-red-50 text-red-600 rounded-sm" title="Ritira">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* ORDINI RICEVUTI */}
      {tab === "ordini" && (
        <div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white border border-[#C9A961]/20 rounded-sm p-5 shadow-sm">
              <div className="text-3xl font-semibold editorial-heading">{orders.length}</div>
              <div className="text-xs text-stone-500 uppercase tracking-wider mt-1">Ordini ricevuti</div>
            </div>
            <div className="bg-white border border-[#C9A961]/20 rounded-sm p-5 shadow-sm">
              <div className="text-3xl font-semibold editorial-heading text-emerald-600">{orders.filter((o) => o.payment_status === "paid").length}</div>
              <div className="text-xs text-stone-500 uppercase tracking-wider mt-1">Pagati</div>
            </div>
            <div className="bg-white border border-[#C9A961]/20 rounded-sm p-5 shadow-sm">
              <div className="text-3xl font-semibold editorial-heading text-[hsl(var(--primary))]">{formatPrice ? formatPrice(ordersRevenue) : `€ ${ordersRevenue}`}</div>
              <div className="text-xs text-stone-500 uppercase tracking-wider mt-1">Incassato (pagati)</div>
            </div>
          </div>

          {orders.length === 0 ? (
            <div className="text-center py-16 bg-white border border-[#C9A961]/20 rounded-sm">
              <Receipt className="h-10 w-10 mx-auto text-stone-400 mb-3" />
              <p className="text-stone-500">Nessun ordine ricevuto per ora.</p>
              <p className="text-sm text-stone-400 mt-1">Gli ordini dei tuoi prodotti a pezzi (B2C) appariranno qui.</p>
            </div>
          ) : (
            <div className="overflow-x-auto border border-[#C9A961]/20 rounded-sm bg-white">
              <table className="w-full text-sm">
                <thead className="bg-stone-50 text-left">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Data</th>
                    <th className="px-4 py-3 font-semibold">Cliente</th>
                    <th className="px-4 py-3 font-semibold">Prodotti</th>
                    <th className="px-4 py-3 font-semibold">Totale</th>
                    <th className="px-4 py-3 font-semibold">Pagamento</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => (
                    <tr key={o.order_id} data-testid="order-row" className="border-t border-stone-200 hover:bg-stone-50 align-top">
                      <td className="px-4 py-3 whitespace-nowrap text-stone-600">
                        {o.created_at ? new Date(o.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium">{o.buyer_name || "Cliente"}</div>
                        {o.buyer_email && <div className="text-xs text-stone-500">{o.buyer_email}</div>}
                      </td>
                      <td className="px-4 py-3">
                        {o.items.map((it, i) => (
                          <div key={i} className="text-stone-700">{it.quantity}× {it.name}</div>
                        ))}
                      </td>
                      <td className="px-4 py-3 font-semibold whitespace-nowrap">{formatPrice ? formatPrice(o.vendor_total) : `€ ${o.vendor_total}`}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded-sm font-semibold ${
                          o.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
                        }`}>
                          {o.payment_status === "paid" ? "Pagato" : "In attesa"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* VETRINA */}
      {tab === "vetrina" && (
        <div className="max-w-2xl bg-card border border-border rounded-sm p-6 md:p-8 space-y-4">
          <div>
            <label className="text-sm font-medium">Nome vetrina</label>
            <input data-testid="store-name-input" className={inputCls} value={store.store_name} onChange={(e) => setStore({ ...store, store_name: e.target.value })} />
          </div>
          <div>
            <label className="text-sm font-medium">Descrizione</label>
            <textarea className={inputCls} rows={4} value={store.store_description} onChange={(e) => setStore({ ...store, store_description: e.target.value })} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Categoria</label>
              <select className={inputCls} value={store.category} onChange={(e) => setStore({ ...store, category: e.target.value })}>
                <option value="">—</option>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium">Città</label>
              <input className={inputCls} value={store.city} onChange={(e) => setStore({ ...store, city: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium">URL logo (opzionale)</label>
            <input className={inputCls} value={store.logo_url} onChange={(e) => setStore({ ...store, logo_url: e.target.value })} placeholder="https://…" />
          </div>
          <button
            data-testid="save-store-btn"
            onClick={saveStore}
            disabled={storeSaving}
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-[hsl(var(--primary))] text-white rounded-sm text-sm font-semibold hover:opacity-90 disabled:opacity-60"
          >
            {storeSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salva vetrina
          </button>
        </div>
      )}

      {/* DOCUMENTI */}
      {tab === "documenti" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Library (platform docs) */}
          <div>
            <h2 className="editorial-heading text-2xl mb-1">Documenti da scaricare</h2>
            <p className="text-sm text-muted-foreground mb-4">Modulistica ufficiale da compilare e firmare.</p>
            <div className="space-y-3">
              {docLibrary.map((d) => (
                <div key={d.id} data-testid="library-doc" className="flex items-center justify-between gap-3 border border-border rounded-sm p-4 bg-card">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="p-2 bg-[hsl(var(--primary))]/10 rounded-sm shrink-0"><FileText className="h-5 w-5 text-[hsl(var(--primary))]" /></div>
                    <div className="min-w-0">
                      <div className="font-medium truncate">{d.label}</div>
                      <div className="text-xs text-muted-foreground">PDF · {fmtSize(d.size_bytes)}</div>
                    </div>
                  </div>
                  <a
                    href={ASSET(d.url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                    data-testid={`download-${d.id}`}
                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-[hsl(var(--primary))] text-white rounded-sm text-xs font-semibold hover:opacity-90 shrink-0"
                  >
                    <Download className="h-4 w-4" /> Scarica
                  </a>
                </div>
              ))}
            </div>
          </div>

          {/* Vendor uploads */}
          <div>
            <h2 className="editorial-heading text-2xl mb-1">I miei documenti</h2>
            <p className="text-sm text-muted-foreground mb-4">Carica contratti firmati o altri documenti (PDF, PNG, JPG · max 25MB).</p>

            <div className="border-2 border-dashed border-border rounded-sm p-5 bg-[hsl(var(--muted))]/30 mb-5">
              <label className="text-sm font-medium block mb-1">Tipo di documento</label>
              <select
                data-testid="doc-type-select"
                value={docType}
                onChange={(e) => setDocType(e.target.value)}
                className={inputCls + " mb-3"}
              >
                <option value="contratto_firmato">Contratto di Licenza firmato</option>
                <option value="liberatoria">Liberatoria firmata</option>
                <option value="altro">Altro documento</option>
              </select>
              <input
                data-testid="doc-label-input"
                value={docLabel}
                onChange={(e) => setDocLabel(e.target.value)}
                placeholder="Etichetta (opzionale) es. Contratto firmato"
                className={inputCls + " mb-3"}
              />
              <input ref={fileInputRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" onChange={uploadDoc} className="hidden" data-testid="doc-file-input" />
              <button
                data-testid="doc-upload-btn"
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                disabled={docUploading}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-[hsl(var(--primary))] text-white rounded-sm text-sm font-semibold hover:opacity-90 disabled:opacity-60"
              >
                {docUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
                {docUploading ? "Caricamento…" : "Carica documento"}
              </button>
            </div>

            {myDocs.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nessun documento caricato.</p>
            ) : (
              <div className="space-y-3">
                {myDocs.map((d) => (
                  <div key={d.id} data-testid="my-doc" className="flex items-center justify-between gap-3 border border-border rounded-sm p-4 bg-card">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="p-2 bg-[hsl(var(--muted))] rounded-sm shrink-0"><FileText className="h-5 w-5 text-muted-foreground" /></div>
                      <div className="min-w-0">
                        <div className="font-medium truncate">{d.label}</div>
                        <div className="text-xs text-muted-foreground truncate">{d.original_name} · {fmtSize(d.size_bytes)}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <a href={ASSET(d.url)} target="_blank" rel="noopener noreferrer" className="p-2 hover:bg-[hsl(var(--muted))] rounded-sm" title="Apri"><Download className="h-4 w-4" /></a>
                      <button data-testid="delete-doc-btn" onClick={() => deleteDoc(d.id)} className="p-2 hover:bg-red-50 text-red-600 rounded-sm" title="Elimina"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* SERVIZI */}
      {tab === "servizi" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <form onSubmit={submitService} className="bg-card border border-border rounded-sm p-6 md:p-8 space-y-4">
            <h2 className="editorial-heading text-2xl">Richiedi un servizio</h2>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                data-testid="svc-marketing"
                onClick={() => setSvc({ ...svc, service_type: "marketing" })}
                className={`flex items-center gap-2 px-4 py-3 rounded-sm border text-sm ${svc.service_type === "marketing" ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5" : "border-border"}`}
              >
                <Megaphone className="h-4 w-4" /> Marketing
              </button>
              <button
                type="button"
                data-testid="svc-sales"
                onClick={() => setSvc({ ...svc, service_type: "sales_assistance" })}
                className={`flex items-center gap-2 px-4 py-3 rounded-sm border text-sm ${svc.service_type === "sales_assistance" ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5" : "border-border"}`}
              >
                <HeadphonesIcon className="h-4 w-4" /> Assistenza vendita
              </button>
            </div>
            <div>
              <label className="text-sm font-medium">Messaggio *</label>
              <textarea data-testid="svc-message" className={inputCls} rows={4} value={svc.message} onChange={(e) => setSvc({ ...svc, message: e.target.value })} placeholder="Descrivi di cosa hai bisogno…" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Budget indicativo (€)</label>
                <input type="number" className={inputCls} value={svc.budget} onChange={(e) => setSvc({ ...svc, budget: e.target.value })} />
              </div>
              <div>
                <label className="text-sm font-medium">Prodotto collegato (opz.)</label>
                <select className={inputCls} value={svc.product_id} onChange={(e) => setSvc({ ...svc, product_id: e.target.value })}>
                  <option value="">—</option>
                  {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
            </div>
            <button data-testid="svc-submit" type="submit" disabled={svcSaving} className="inline-flex items-center gap-2 px-6 py-2.5 bg-[hsl(var(--primary))] text-white rounded-sm text-sm font-semibold hover:opacity-90 disabled:opacity-60">
              {svcSaving && <Loader2 className="h-4 w-4 animate-spin" />} Invia richiesta
            </button>
          </form>

          <div>
            <h2 className="editorial-heading text-2xl mb-4">Le mie richieste</h2>
            {services.length === 0 ? (
              <p className="text-muted-foreground">Nessuna richiesta inviata.</p>
            ) : (
              <div className="space-y-3">
                {services.map((s) => (
                  <div key={s.id} data-testid="service-row" className="border border-border rounded-sm p-4 bg-card">
                    <div className="flex items-center justify-between">
                      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[hsl(var(--primary))]">
                        {s.service_type === "marketing" ? <Megaphone className="h-3.5 w-3.5" /> : <HeadphonesIcon className="h-3.5 w-3.5" />}
                        {s.service_type === "marketing" ? "Marketing" : "Assistenza vendita"}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-sm ${
                        s.status === "done" ? "bg-emerald-100 text-emerald-700"
                        : s.status === "rejected" ? "bg-red-100 text-red-700"
                        : s.status === "in_progress" ? "bg-blue-100 text-blue-700"
                        : "bg-gray-100 text-gray-600"
                      }`}>{s.status}</span>
                    </div>
                    <p className="text-sm mt-2 text-foreground/80">{s.message}</p>
                    {s.budget != null && <div className="text-xs text-muted-foreground mt-1">Budget: € {s.budget}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

        </div>
      </div>

      {/* PRODUCT DIALOG */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Modifica prodotto" : "Nuovo prodotto"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Nome *</label>
              <input data-testid="pf-name" className={inputCls} value={pform.name} onChange={set("name")} />
            </div>
            <div>
              <label className="text-sm font-medium">Descrizione</label>
              <textarea className={inputCls} rows={2} value={pform.description} onChange={set("description")} />
            </div>
            <div>
              <label className="text-sm font-medium">Categoria</label>
              <select className={inputCls} value={pform.category} onChange={set("category")}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            {/* Immagine prodotto — upload file */}
            <div>
              <label className="text-sm font-medium block mb-1">Immagine prodotto</label>
              <div className="flex items-center gap-3">
                <div className="w-20 h-20 rounded-sm border border-border bg-[hsl(var(--muted))] overflow-hidden flex items-center justify-center shrink-0">
                  {pform.image_url ? (
                    <img src={ASSET(pform.image_url)} alt="anteprima" className="w-full h-full object-cover" />
                  ) : (
                    <ImageIcon className="h-6 w-6 text-muted-foreground" />
                  )}
                </div>
                <div className="flex-1">
                  <input ref={imgInputRef} type="file" accept=".png,.jpg,.jpeg,.webp,.gif" onChange={uploadProductImage} className="hidden" data-testid="pf-image-file" />
                  <button
                    type="button"
                    data-testid="pf-image-upload"
                    onClick={() => imgInputRef.current && imgInputRef.current.click()}
                    disabled={imgUploading}
                    className="inline-flex items-center gap-2 px-4 py-2 border border-[hsl(var(--primary))] text-[hsl(var(--primary))] rounded-sm text-sm font-semibold hover:bg-[hsl(var(--primary))]/5 disabled:opacity-60"
                  >
                    {imgUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
                    {pform.image_url ? "Cambia immagine" : "Carica immagine"}
                  </button>
                  {pform.image_url && (
                    <button type="button" data-testid="pf-image-remove" onClick={() => setPform((f) => ({ ...f, image_url: "" }))} className="ml-2 text-xs text-red-600 hover:underline">Rimuovi</button>
                  )}
                  <p className="text-[11px] text-muted-foreground mt-1">PNG, JPG, WEBP · max 10MB</p>
                </div>
              </div>
            </div>

            {/* Sale mode — only choosable on create */}
            <div>
              <label className="text-sm font-medium mb-1 block">Modalità di vendita *</label>
              {editing ? (
                <div className="text-sm px-4 py-2.5 border border-border rounded-sm bg-[hsl(var(--muted))]">
                  {pform.sale_mode === "blocchi" ? "A blocchi (B2B)" : "A pezzi (B2C)"} — non modificabile
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <button type="button" data-testid="pf-mode-pezzi" onClick={() => setPform({ ...pform, sale_mode: "pezzi" })}
                    className={`flex flex-col items-start gap-1 px-4 py-3 rounded-sm border text-left ${pform.sale_mode === "pezzi" ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5" : "border-border"}`}>
                    <span className="flex items-center gap-1.5 text-sm font-semibold"><Package className="h-4 w-4" /> A pezzi</span>
                    <span className="text-[11px] text-muted-foreground">Vendita B2C, al pezzo</span>
                  </button>
                  <button type="button" data-testid="pf-mode-blocchi" onClick={() => setPform({ ...pform, sale_mode: "blocchi" })}
                    className={`flex flex-col items-start gap-1 px-4 py-3 rounded-sm border text-left ${pform.sale_mode === "blocchi" ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5" : "border-border"}`}>
                    <span className="flex items-center gap-1.5 text-sm font-semibold"><Boxes className="h-4 w-4" /> A blocchi</span>
                    <span className="text-[11px] text-muted-foreground">Solo B2B, a blocchi</span>
                  </button>
                </div>
              )}
            </div>

            {pform.sale_mode === "blocchi" && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Pezzi per blocco *</label>
                  <input data-testid="pf-blocksize" type="number" min="1" className={inputCls} value={pform.block_size} onChange={set("block_size")} />
                </div>
                <div>
                  <label className="text-sm font-medium">Blocchi minimi per ordine</label>
                  <input type="number" min="1" className={inputCls} value={pform.min_order_blocks} onChange={set("min_order_blocks")} />
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Prezzo (€) *</label>
                <input data-testid="pf-price" type="number" step="0.01" min="0" className={inputCls} value={pform.price} onChange={set("price")} />
              </div>
              <div>
                <label className="text-sm font-medium">Quantità *</label>
                <input data-testid="pf-quantity" type="number" min="0" className={inputCls} value={pform.quantity} onChange={set("quantity")} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setDialogOpen(false)} className="px-5 py-2.5 border border-border rounded-sm text-sm">Annulla</button>
            <button data-testid="pf-save" onClick={saveProduct} disabled={saving} className="inline-flex items-center gap-2 px-6 py-2.5 bg-[hsl(var(--primary))] text-white rounded-sm text-sm font-semibold hover:opacity-90 disabled:opacity-60">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} {editing ? "Salva" : "Pubblica"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
